import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { healthCommitteeApi } from "../../services/healthCommitteeApi";
import { employeeApi } from "../../services/employeeApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const ROLES = ["MANAGEMENT_REPRESENTATIVE", "EMPLOYEE_REPRESENTATIVE", "INDUSTRIAL_PHYSICIAN", "OTHER"];
const EMPTY_FORM = { employee_id: "", role: "MANAGEMENT_REPRESENTATIVE", term_start: "", term_end: "" };

export default function HealthCommitteeMembersPage() {
  const { t } = useLocalization();
  const { canViewHealthCommittee, canManageHealthCommittee } = usePermissions();
  const [members, setMembers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    healthCommitteeApi.listMembers().then((r) => setMembers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (canViewHealthCommittee) { load(); employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {}); } }, [canViewHealthCommittee, load]);

  if (!canViewHealthCommittee) return <p className="text-sm text-gray-500" data-testid="health-committee-access-denied">{t("common.accessDenied")}</p>;

  const nameOf = (id) => employees.find((e) => e.id === id)?.legal_name || id;

  const addMember = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.term_start) return;
    setBusy(true);
    try {
      await healthCommitteeApi.addMember({ ...form, term_end: form.term_end || null });
      toast.success(t("mgmtHealthCommittee.memberAdded"));
      setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (member) => {
    setBusy(true);
    try {
      await healthCommitteeApi.updateMember(member.id, { status: member.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      toast.success(t("mgmtHealthCommittee.memberStatusUpdated"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="health-committee-members-page">
      <PageHelpBanner pageKey="health-committee-members" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtHealthCommittee.membersHelp") }} />
      </PageHelpBanner>
      <div>
        <Link to="/management/compliance/health-committee" className="text-xs font-semibold text-blue-600 hover:underline" data-testid="health-committee-back-link">&larr; {t("mgmtHealthCommittee.backToDashboard")}</Link>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 mt-1">{t("mgmtHealthCommittee.committeeMembers")}</h2>
      </div>

      {canManageHealthCommittee && (
        <form onSubmit={addMember} className="bg-white rounded-xl border border-gray-200 p-5 flex flex-wrap gap-2 items-end" data-testid="hc-add-member-form">
          <select data-testid="hc-member-employee-select" value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">{t("mgmtPayroll.employeePlaceholder")}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
          </select>
          <select data-testid="hc-member-role-select" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <input data-testid="hc-member-term-start-input" type="date" value={form.term_start} onChange={(e) => setForm((f) => ({ ...f, term_start: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtHealthCommittee.termStart")} />
          <input data-testid="hc-member-term-end-input" type="date" value={form.term_end} onChange={(e) => setForm((f) => ({ ...f, term_end: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder={t("mgmtHealthCommittee.termEndOptional")} />
          <button data-testid="hc-add-member-btn" disabled={busy || !form.employee_id || !form.term_start} type="submit" className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{t("mgmtHealthCommittee.addMember")}</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colRole")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colTerm")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t border-gray-100" data-testid={`hc-member-row-${m.id}`}>
                <td className="px-4 py-2 text-gray-700">{nameOf(m.employee_id)}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.role.replace(/_/g, " ")}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{m.term_start} → {m.term_end || t("mgmtHealthCommittee.ongoing")}</td>
                <td className="px-4 py-2"><StatusBadge status={m.status} /></td>
                <td className="px-4 py-2">
                  {canManageHealthCommittee && (
                    <button data-testid={`hc-member-toggle-status-${m.id}`} disabled={busy} onClick={() => toggleStatus(m)} className="text-xs font-semibold text-blue-600 hover:underline">
                      {m.status === "ACTIVE" ? t("mgmtHealthCommittee.deactivate") : t("mgmtHealthCommittee.reactivate")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-hc-members">{t("mgmtHealthCommittee.noMembersRecorded")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
